import { GROCERY_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosGroceryInstance from './AxiosGroceryInstance';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { authHeaders, mergeRestaurantUserRows } from './restaurantApi';
import { fetchActiveSupermarkets } from './groceryApi';
import { getActiveStoreId } from './entityContext';
import { isDeliveryAgentUser } from './deliveryAgentUtils';

export const ENTITY_USER_TYPE_STORE = 'STR';

function groceryAuthHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function extractStoreId(item) {
  return (
    item?.supermarketId ??
    item?.storeId ??
    item?.Store_Id ??
    item?.id ??
    null
  );
}

export function extractStoreName(item) {
  return item?.supermarketName ?? item?.storeName ?? item?.Store_Name ?? '';
}

export async function fetchStoreUsers(storeId) {
  const response = await axiosGroceryInstance.get(
    GROCERY_API_PATHS.storeUsersByStoreId(storeId),
    { headers: groceryAuthHeaders() }
  );
  return mergeRestaurantUserRows(response.data?.users || []);
}

/** Store staff with Delivery_Agent role. */
export async function fetchStoreDeliveryAgents(storeId) {
  const users = await fetchStoreUsers(storeId);
  return users.filter(isDeliveryAgentUser);
}

/** Restaurant delivery agents (role id 5) — optional restaurantId filter. */
export async function fetchRestaurantDeliveryAgents(restaurantId = null) {
  const params = {};
  if (restaurantId != null && restaurantId !== '' && restaurantId !== 'null') {
    params.restaurantId = restaurantId;
  }
  try {
    const response = await axiosRestaurantInstance.get(
      RESTAURANT_API_PATHS.restaurantsDeliveryAgents,
      { params, headers: authHeaders() }
    );
    const payload = response.data;
    const list = Array.isArray(payload?.users)
      ? payload.users
      : Array.isArray(payload?.Users)
        ? payload.Users
        : Array.isArray(payload)
          ? payload
          : [];
    return list;
  } catch (err) {
    if (err?.response?.status === 804) return [];
    throw err;
  }
}

export async function createStoreUser({
  storeId,
  storeName,
  firstName,
  lastName,
  mobile,
  email,
  aadharNo,
  password,
  entityRoleIds,
  roleNames,
  createdById,
}) {
  const payload = {
    type: ENTITY_USER_TYPE_STORE,
    storeId,
    storeName,
    firstName,
    lastName,
    mobile,
    email,
    aadharNumber: aadharNo,
    password,
    entityRoleIds: entityRoleIds || [],
    roleNames,
    createdById,
  };
  return axiosGroceryInstance.post(GROCERY_API_PATHS.storeUsers, payload, {
    headers: { ...groceryAuthHeaders(), 'Content-Type': 'application/json' },
  });
}

export async function updateStoreUser(userId, payload) {
  return axiosRestaurantInstance.put(
    RESTAURANT_API_PATHS.restaurantUserById(userId),
    { ...payload, type: ENTITY_USER_TYPE_STORE },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
}

export async function deactivateStoreUser(
  targetUserId,
  { storeId, storeName, updatedById, userRow }
) {
  if (!userRow) {
    throw new Error('User not found');
  }

  return updateStoreUser(targetUserId, {
    firstName: userRow.firstName,
    lastName: userRow.lastName,
    mobile: userRow.mobile,
    email: userRow.email,
    aadharNo: userRow.aadharNo,
    storeId: String(storeId),
    storeName: storeName || '',
    entityRoleIds: userRow.entityRoleIds || [],
    roleNames: userRow.roles || [],
    status: 'INACTIVE',
    updatedById,
    fcmToken: null,
  });
}

export async function resolveInitialStore(userId, list = null) {
  const stores = list ?? (await fetchActiveSupermarkets(userId));
  const storedId = getActiveStoreId();
  if (storedId && stores.some((s) => String(extractStoreId(s)) === String(storedId))) {
    return stores.find((s) => String(extractStoreId(s)) === String(storedId)) || null;
  }
  const defaultStore = stores.find((s) => s.isDefault) || stores[0];
  return defaultStore || null;
}
